import { PROD, URLS, getEnv } from '@/data/config';
import { usePaymentFlagsStore } from '@/feature/devsettings/hooks/use-payment-flags-store';
import { createActiveWalletJurisdictionHeaders, useActiveWallet } from '@/hooks/use-active-wallet';
import { isWeb } from '@/utils/constants-platform-specific';
import { isErrorResponse } from '@/utils/errors/response-handler';
import { logger } from '@/utils/logging';
import { useMutation } from '@tanstack/react-query';

import { PaymentMethodTypes } from '../const';
import { useDepositMatchPromo } from './use-deposit-match-promo';
import { PaymentMethod } from './use-deposit-payment-methods';
import { useDepositStatusStore } from './use-deposit-status-store';

const LOG_TAG = '[DEPOSIT_URL_FETCH]';

export type PaymentParams = {
    currency: string;
    type: PaymentMethod['type'];
    payment_token?: string;
    payment_type_id?: string;
    transaction_id?: string;
};

export type DepositParams = PaymentParams & {
    amount: string;
};

export type PurchaseParams = PaymentParams & {
    coin_package_id: string;
};

export const getDepositHeaders = (type: PaymentMethod['type']) => {
    // dev only, makes Paysafe return its simulated ERROR/FAILED webhooks instead of a real deposit
    // never on prod, the dev menu is open there for @betr.app accounts
    const isPaypalSimulatorEnabled =
        getEnv() !== PROD &&
        type === PaymentMethodTypes.PaysafePaypal &&
        usePaymentFlagsStore.getState().paymentFlags.paypalErrorSimulator?.enabled;

    return {
        ...createActiveWalletJurisdictionHeaders(),
        'Content-Type': 'application/x-www-form-urlencoded',
        'DYNAMIC-REDIRECT': String(
            type === PaymentMethodTypes.PaysafeMazooma ||
                type === PaymentMethodTypes.PaysafePayByBank ||
                type === PaymentMethodTypes.PaysafeVenmo
        ),
        'X-Agent-Source': isWeb ? 'web' : 'mobile',
        ...(isPaypalSimulatorEnabled ? { SIMULATOR: 'INTERNAL' } : {}),
    };
};

/**
 * Sends a request to deposit API endpoint.
 *
 * @async
 * @param {Object} payload - The data payload for the API call.
 * @param {number} payload.selectedAmount - The selected amount for deposit.
 * @param {PaymentMethod} payload.selectedPaymentMethodType - The selected payment method type.
 * @param {string} payload.currency - The currency of the amount.
 * @returns {Promise} Promise object represents the data returned from the API.
 */
const getPaymentUrl = async (payload: DepositParams): Promise<DepositResponseData> => {
    const { type, amount, currency, payment_token, payment_type_id, transaction_id } = payload;

    const params = new URLSearchParams({
        type,
        amount,
        currency,
        ...(payment_token && { payment_token }),
        ...(payment_type_id && { payment_type_id }),
        ...(transaction_id && { transaction_id }),
    });

    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/wallet/deposit`, {
        method: 'POST',
        headers: getDepositHeaders(type),
        body: params.toString(),
    });

    const jsonResponse = await response.json();
    if (!response.ok) {
        throw jsonResponse;
    }

    return jsonResponse.data;
};

/**
 * Sends a request to purchase API endpoint.
 *
 */
const getPurchaseResult = async (payload: PurchaseParams): Promise<PurchaseResponseData> => {
    const { coin_package_id, type, payment_token, payment_type_id, transaction_id } = payload;

    const params = new URLSearchParams({
        coin_package_id,
        type,
        ...(payment_token && { payment_token }),
        ...(payment_type_id && { payment_type_id }),
        ...(transaction_id && { transaction_id }),
    });

    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/wallet/purchase`, {
        method: 'POST',
        headers: getDepositHeaders(type),
        body: params.toString(),
    });

    const jsonResponse = await response.json();
    if (!response.ok) {
        throw jsonResponse;
    }

    return jsonResponse.data;
};

export type DepositResponseData = {
    extra: {
        payment_handle_id: string;
        user_wallet_id: string;
    };
    payment_url: string;
    status: string;
    token: string;
    is_restricted_bin?: boolean;
};

export type PurchaseResponseData = DepositResponseData & {
    coin_package: {
        coin_package_id: number;
        coin_amount: number;
        cash_amount: number;
        real_money_price: number;
        expiry_in_hours: number | null;
        real_money_currency: string;
        is_active: boolean;
        is_ftd_only: boolean;
        sort_order: number;
        created_at: string;
        updated_at: string;
        deleted_at: string | null;
    };
};

export type DepositErrorResponse = Error & {
    error_codes: string[];
    fields: string[];
    errors: {
        error_code: string;
    };
    message: string;
};

/**
 * Custom hook to perform a mutation for deposit operation.
 * In case of an error, it logs the error details.
 *
 * @returns The mutation object from `react-query` with methods to mutate, check the status of the mutation, etc.
 */
export const useDeposit = () => {
    const { isSharedSocialWallet } = useActiveWallet();
    const selectedAmount = useDepositStatusStore(state => state.selectedAmount);
    const coinPackageId = useDepositStatusStore(state => state.coinPackageId);
    const { activatePromoIfExists, clearPromo } = useDepositMatchPromo();

    return useMutation({
        mutationKey: ['getPaymentUrl'],
        mutationFn: async (params: PaymentParams) => {
            // Activate promo first if it exists then clear it from the store
            await activatePromoIfExists(LOG_TAG);

            // For sweeps products, we call purchase endpoint instead of deposit
            return isSharedSocialWallet
                ? getPurchaseResult({ ...params, coin_package_id: coinPackageId })
                : getPaymentUrl({ ...params, amount: selectedAmount });
        },
        onError: (err: DepositErrorResponse, variables) => {
            if (isErrorResponse(err)) {
                logger.warn(LOG_TAG, { ...err, ...variables });
            } else {
                const errorMessage = `An unknown error occurred while fetching deposit URL: ${String(err)}`;
                logger.warn(LOG_TAG, { message: errorMessage });
            }
        },
        onSettled: () => {
            clearPromo();
        },
    });
};
